import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Zap,
  Brain,
  MessageSquare,
  BarChart3,
  Calendar,
  CheckCircle,
  Clock,
  TrendingUp,
  FileText,
  Sparkles,
  Send,
  Paperclip,
  Mic,
  ChevronDown,
  Plus,
  Minus,
} from 'lucide-react';
import {
  AriaCharacter,
  NovaCharacter,
  AtlasCharacter,
  CharacterWrapper,
} from './AICharacters';
import { useThemeStore } from '../../store/theme';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

// Dashboard Stats Preview
export function DashboardStatsPreview() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';

  const stats = [
    { label: 'Active Tasks', value: '247', change: '+12%', color: 'text-green-400' },
    { label: 'Messages Today', value: '1,842', change: '+8%', color: 'text-green-400' },
    { label: 'Credits Used', value: '4,521', change: '45%', color: 'text-cyan-400' },
    { label: 'AI Employees', value: '15', change: 'All Active', color: 'text-purple-400' },
  ];

  return (
    <section className="py-24 lg:py-32 relative overflow-hidden">
      <div className="absolute inset-0">
        <div className={`absolute top-1/4 right-0 w-[500px] h-[500px] rounded-full blur-3xl ${isDark ? 'bg-purple-500/10' : 'bg-purple-500/5'}`} />
        <div className={`absolute bottom-1/4 left-0 w-[400px] h-[400px] rounded-full blur-3xl ${isDark ? 'bg-cyan-500/10' : 'bg-cyan-500/5'}`} />
      </div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          className="text-center mb-12"
        >
          <motion.div variants={fadeInUp} className="inline-flex items-center px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/20 mb-6">
            <BarChart3 className="w-4 h-4 text-purple-400 mr-2" />
            <span className="text-sm text-purple-400">Real-time Analytics</span>
          </motion.div>
          <motion.h2 variants={fadeInUp} className={`text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
            Track Everything in{' '}
            <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
              One Place
            </span>
          </motion.h2>
          <motion.p variants={fadeInUp} className={`text-lg max-w-2xl mx-auto ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
            Get a bird's eye view of all your AI employee activities with our intuitive dashboard.
          </motion.p>
        </motion.div>

        {/* Browser Mockup */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.8 }}
          className="relative"
        >
          {/* Browser Frame */}
          <div className={`rounded-2xl backdrop-blur-xl border overflow-hidden shadow-2xl ${isDark ? 'bg-dark-800/80 border-white/10' : 'bg-white border-slate-200'}`}>
            {/* Browser Header */}
            <div className={`flex items-center justify-between px-3 sm:px-4 py-2 sm:py-3 border-b ${isDark ? 'bg-dark-900/50 border-white/5' : 'bg-slate-50 border-slate-200'}`}>
              <div className="flex items-center gap-1.5 sm:gap-2">
                <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-red-500" />
                <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-yellow-500" />
                <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-green-500" />
              </div>
              <div className={`flex items-center gap-1.5 sm:gap-2 px-2 sm:px-4 py-1 sm:py-1.5 rounded-lg border max-w-[60%] sm:max-w-none ${isDark ? 'bg-dark-700/50 border-white/5' : 'bg-white border-slate-200'}`}>
                <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 text-purple-400 flex-shrink-0" />
                <span className={`text-xs sm:text-sm truncate ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>sweetpilot.ai/dashboard</span>
              </div>
              <div className="w-10 sm:w-20" />
            </div>

            {/* Dashboard Content */}
            <div className="p-6">
              {/* Stats Grid */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                {stats.map((stat, index) => (
                  <motion.div
                    key={stat.label}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true, amount: 0.1 }}
                    transition={{ delay: index * 0.1 }}
                    className={`p-4 rounded-xl border ${isDark ? 'bg-dark-700/50 border-white/5' : 'bg-slate-50 border-slate-200'}`}
                  >
                    <p className={`text-sm mb-1 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>{stat.label}</p>
                    <p className={`text-2xl lg:text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{stat.value}</p>
                    <p className={`text-sm ${stat.color}`}>{stat.change}</p>
                  </motion.div>
                ))}
              </div>

              {/* Recent Activity Card */}
              <div className={`p-4 rounded-xl border ${isDark ? 'bg-dark-700/50 border-white/5' : 'bg-slate-50 border-slate-200'}`}>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center text-xl">
                    🎀
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Aria</span>
                      <span className={`text-sm ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>Social Media Manager</span>
                      <span className="px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 text-xs">Online</span>
                    </div>
                    <p className={isDark ? 'text-gray-300' : 'text-slate-600'}>
                      I've completed your content calendar for next week! 7 posts ready for Instagram and Twitter with engaging captions and hashtags. Would you like me to schedule them?
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Glow Effect */}
          <div className={`absolute -inset-4 bg-gradient-to-r from-purple-500/20 via-transparent to-cyan-500/20 rounded-3xl blur-2xl -z-10 ${isDark ? '' : 'opacity-50'}`} />
        </motion.div>
      </div>
    </section>
  );
}

// Chat Interface Preview - Functional Demo with Ink
export function ChatInterfacePreview() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const [messages, setMessages] = useState<Array<{
    role: 'user' | 'assistant';
    content: string;
    time: string;
  }>>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [promptsRemaining, setPromptsRemaining] = useState(2);
  const [showSignUpPrompt, setShowSignUpPrompt] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Get API base URL (strip trailing /api if present to avoid double /api)
  const getApiBase = () => {
    const url = import.meta.env.VITE_API_URL || '';
    return url.replace(/\/api$/, '');
  };

  // Generate or retrieve session ID
  const getSessionId = () => {
    const key = 'sweetpilot_demo_session';
    let sessionId = localStorage.getItem(key);
    if (!sessionId) {
      sessionId = `demo_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
      localStorage.setItem(key, sessionId);
    }
    return sessionId;
  };

  // Check session status on mount
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const sessionId = getSessionId();
        const response = await fetch(`${getApiBase()}/api/demo/status/${sessionId}`);
        const data = await response.json() as { success: boolean; data?: { prompts_remaining: number; limit_reached: boolean } };
        if (data.success && data.data) {
          setPromptsRemaining(data.data.prompts_remaining);
          if (data.data.limit_reached) {
            setShowSignUpPrompt(true);
          }
        }
      } catch (error) {
        console.error('Failed to check demo status:', error);
      }
    };
    checkStatus();
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingContent]);

  const formatTime = () => {
    return new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    if (promptsRemaining <= 0) {
      setShowSignUpPrompt(true);
      return;
    }

    const userMessage = inputValue.trim();
    setInputValue('');
    setIsLoading(true);
    setStreamingContent('');

    // Add user message
    setMessages(prev => [...prev, {
      role: 'user',
      content: userMessage,
      time: formatTime(),
    }]);

    try {
      const sessionId = getSessionId();
      const response = await fetch(`${getApiBase()}/api/demo/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: userMessage, sessionId }),
      });

      if (response.status === 429) {
        setShowSignUpPrompt(true);
        setPromptsRemaining(0);
        setIsLoading(false);
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to get response');
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let fullContent = '';

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6);
              if (data === '[DONE]') continue;

              try {
                const parsed = JSON.parse(data);
                if (parsed.content) {
                  fullContent += parsed.content;
                  setStreamingContent(fullContent);
                }
                if (parsed.done) {
                  setPromptsRemaining(parsed.prompts_remaining || 0);
                  if (parsed.prompts_remaining === 0) {
                    setShowSignUpPrompt(true);
                  }
                }
              } catch {
                // Ignore parsing errors
              }
            }
          }
        }
      }

      // Add assistant message
      if (fullContent) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: fullContent,
          time: formatTime(),
        }]);
      }
      setStreamingContent('');
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I had trouble processing that. Please try again.',
        time: formatTime(),
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const suggestedPrompts = [
    'Write a catchy tagline for a coffee shop',
    'Create a LinkedIn post about AI',
    'Write a product description for headphones',
  ];

  return (
    <section className="py-24 lg:py-32 relative overflow-hidden">
      <div className="absolute inset-0">
        <div className={`absolute top-1/3 left-1/4 w-[400px] h-[400px] rounded-full blur-3xl ${isDark ? 'bg-indigo-500/10' : 'bg-indigo-500/5'}`} />
      </div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Text Content */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
          >
            <motion.div variants={fadeInUp} className="inline-flex items-center px-4 py-2 rounded-full bg-indigo-500/10 border border-indigo-500/20 mb-6">
              <MessageSquare className="w-4 h-4 text-indigo-400 mr-2" />
              <span className="text-sm text-indigo-400">Try It Now - 2 Free Prompts</span>
            </motion.div>
            <motion.h2 variants={fadeInUp} className={`text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Chat Like You're Talking to{' '}
              <span className="bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">
                Real Experts
              </span>
            </motion.h2>
            <motion.p variants={fadeInUp} className={`text-lg mb-8 ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
              Our AI employees understand context, remember your preferences, and deliver results that feel genuinely human. Try Ink, our AI copywriter, right now!
            </motion.p>
            <motion.div variants={fadeInUp} className="space-y-4">
              {[
                'Context-aware responses that remember your history',
                'Multiple AI personalities for different needs',
                'Real-time streaming for instant feedback',
                'Rich formatting with markdown support',
              ].map((feature) => (
                <div key={feature} className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span className={isDark ? 'text-gray-300' : 'text-slate-600'}>{feature}</span>
                </div>
              ))}
            </motion.div>
          </motion.div>

          {/* Interactive Chat */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className={`rounded-2xl backdrop-blur-xl border overflow-hidden shadow-2xl ${isDark ? 'bg-dark-800/80 border-white/10' : 'bg-white border-slate-200'}`}>
              {/* Chat Header */}
              <div className={`flex items-center justify-between p-4 border-b ${isDark ? 'border-white/5 bg-dark-900/50' : 'border-slate-100 bg-slate-50'}`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center text-lg">
                    ✍️
                  </div>
                  <div>
                    <p className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Ink</p>
                    <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>Copywriter • Online</p>
                  </div>
                </div>
                <div className={`px-3 py-1 rounded-full text-xs font-medium ${promptsRemaining > 0 ? 'bg-green-500/20 text-green-400' : 'bg-orange-500/20 text-orange-400'}`}>
                  {promptsRemaining} prompt{promptsRemaining !== 1 ? 's' : ''} left
                </div>
              </div>

              {/* Messages */}
              <div className="p-4 space-y-4 h-[350px] overflow-y-auto">
                {messages.length === 0 && !streamingContent && (
                  <div className="flex flex-col items-center justify-center h-full text-center px-4">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500/20 to-violet-500/20 flex items-center justify-center text-3xl mb-4">
                      ✍️
                    </div>
                    <p className={`font-medium mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      Hi! I'm Ink, your AI copywriter
                    </p>
                    <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                      Ask me to write anything - taglines, social posts, emails, and more!
                    </p>
                    <div className="flex flex-wrap gap-2 justify-center">
                      {suggestedPrompts.map((prompt) => (
                        <button
                          key={prompt}
                          onClick={() => {
                            setInputValue(prompt);
                          }}
                          className={`px-3 py-1.5 rounded-full text-xs transition-all ${isDark ? 'bg-dark-700 hover:bg-dark-600 text-gray-300' : 'bg-slate-100 hover:bg-slate-200 text-slate-600'}`}
                        >
                          {prompt}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {messages.map((msg, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    {msg.role === 'assistant' && (
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center text-sm mr-2 shrink-0">
                        ✍️
                      </div>
                    )}
                    <div
                      className={`max-w-[80%] p-3 rounded-2xl ${
                        msg.role === 'user'
                          ? 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white'
                          : isDark
                            ? 'bg-dark-700/50 border border-white/5 text-gray-300'
                            : 'bg-slate-100 border border-slate-200 text-slate-700'
                      }`}
                    >
                      <p className="whitespace-pre-line text-sm">{msg.content}</p>
                      <p className={`text-xs mt-2 ${msg.role === 'user' ? 'text-white/70' : isDark ? 'text-gray-500' : 'text-slate-500'}`}>
                        {msg.time}
                      </p>
                    </div>
                  </motion.div>
                ))}

                {/* Streaming message */}
                {streamingContent && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex justify-start"
                  >
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center text-sm mr-2 shrink-0">
                      ✍️
                    </div>
                    <div className={`max-w-[80%] p-3 rounded-2xl ${isDark ? 'bg-dark-700/50 border border-white/5 text-gray-300' : 'bg-slate-100 border border-slate-200 text-slate-700'}`}>
                      <p className="whitespace-pre-line text-sm">{streamingContent}</p>
                    </div>
                  </motion.div>
                )}

                {/* Loading indicator */}
                {isLoading && !streamingContent && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex justify-start"
                  >
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center text-sm mr-2 shrink-0">
                      ✍️
                    </div>
                    <div className={`p-3 rounded-2xl ${isDark ? 'bg-dark-700/50 border border-white/5' : 'bg-slate-100 border border-slate-200'}`}>
                      <div className="flex gap-1">
                        <div className="w-2 h-2 rounded-full bg-indigo-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                        <div className="w-2 h-2 rounded-full bg-indigo-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                        <div className="w-2 h-2 rounded-full bg-indigo-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </motion.div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Sign Up Prompt Overlay */}
              <AnimatePresence>
                {showSignUpPrompt && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="absolute inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm rounded-2xl"
                  >
                    <motion.div
                      initial={{ scale: 0.9, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      className={`max-w-sm mx-4 p-6 rounded-2xl text-center ${isDark ? 'bg-dark-800 border border-white/10' : 'bg-white border border-slate-200'}`}
                    >
                      <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center">
                        <Sparkles className="w-8 h-8 text-white" />
                      </div>
                      <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        Unlock Unlimited Access
                      </h3>
                      <p className={`text-sm mb-6 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                        You've used your 2 free prompts. Sign up now to get unlimited conversations with all 15 AI employees!
                      </p>
                      <a
                        href="/register"
                        className="inline-flex items-center justify-center w-full px-6 py-3 rounded-xl bg-gradient-to-r from-purple-500 to-indigo-500 text-white font-medium hover:shadow-lg hover:shadow-purple-500/25 transition-all"
                      >
                        Get Started Free
                      </a>
                      <p className={`text-xs mt-3 ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
                        No credit card required
                      </p>
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Input */}
              <div className={`p-4 border-t ${isDark ? 'border-white/5 bg-dark-900/30' : 'border-slate-100 bg-slate-50'}`}>
                <div className={`flex items-center gap-2 p-3 rounded-xl border ${isDark ? 'bg-dark-700/50 border-white/5' : 'bg-white border-slate-200'}`}>
                  <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={promptsRemaining > 0 ? "Ask Ink to write something..." : "Sign up to continue..."}
                    disabled={isLoading || promptsRemaining <= 0}
                    className={`flex-1 bg-transparent text-sm focus:outline-none ${isDark ? 'text-white placeholder-gray-500' : 'text-slate-900 placeholder-slate-400'} disabled:opacity-50`}
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={!inputValue.trim() || isLoading || promptsRemaining <= 0}
                    className="p-2 rounded-lg bg-gradient-to-r from-purple-500 to-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
                  >
                    <Send className="w-4 h-4 text-white" />
                  </button>
                </div>
              </div>
            </div>

            {/* Glow */}
            <div className={`absolute -inset-4 bg-gradient-to-r from-indigo-500/20 via-transparent to-purple-500/20 rounded-3xl blur-2xl -z-10 ${isDark ? '' : 'opacity-50'}`} />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// Task Automation Preview
export function TaskAutomationPreview() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';

  const tasks = [
    { title: 'Weekly Social Media Report', employee: 'Aria', avatar: '🎀', status: 'completed', time: 'Every Monday, 9 AM' },
    { title: 'Customer Support Summary', employee: 'Nova', avatar: '💙', status: 'running', time: 'Daily, 6 PM' },
    { title: 'Competitor Analysis', employee: 'Atlas', avatar: '📊', status: 'scheduled', time: 'Every Friday, 10 AM' },
    { title: 'Email Campaign Draft', employee: 'Echo', avatar: '📧', status: 'completed', time: 'Every Tuesday, 8 AM' },
  ];

  return (
    <section className="py-24 lg:py-32 relative overflow-hidden">
      <div className="absolute inset-0">
        <div className={`absolute bottom-1/4 right-1/4 w-[500px] h-[500px] rounded-full blur-3xl ${isDark ? 'bg-emerald-500/10' : 'bg-emerald-500/5'}`} />
      </div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Task List Mockup */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.8 }}
            className="relative order-2 lg:order-1"
          >
            <div className={`rounded-2xl backdrop-blur-xl border overflow-hidden shadow-2xl ${isDark ? 'bg-dark-800/80 border-white/10' : 'bg-white border-slate-200'}`}>
              {/* Header */}
              <div className={`flex items-center justify-between p-4 border-b ${isDark ? 'border-white/5 bg-dark-900/50' : 'border-slate-100 bg-slate-50'}`}>
                <div className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-emerald-400" />
                  <span className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Automated Tasks</span>
                </div>
                <button className="px-3 py-1.5 rounded-lg bg-emerald-500/20 text-emerald-400 text-sm font-medium">
                  + New Task
                </button>
              </div>

              {/* Tasks */}
              <div className="p-3 sm:p-4 space-y-2 sm:space-y-3">
                {tasks.map((task, index) => (
                  <motion.div
                    key={task.title}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, amount: 0.1 }}
                    transition={{ delay: index * 0.1 }}
                    className={`flex items-start sm:items-center gap-2 sm:gap-4 p-2 sm:p-3 rounded-xl border transition-colors ${isDark ? 'bg-dark-700/50 border-white/5 hover:border-white/10' : 'bg-slate-50 border-slate-200 hover:border-slate-300'}`}
                  >
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-gradient-to-br from-purple-500/20 to-cyan-500/20 flex items-center justify-center text-base sm:text-lg flex-shrink-0">
                      {task.avatar}
                    </div>
                    <div className="flex-1 min-w-0 overflow-hidden">
                      <p className={`font-medium text-sm sm:text-base truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>{task.title}</p>
                      <div className={`flex flex-wrap items-center gap-1 sm:gap-2 text-xs sm:text-sm ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>
                        <span>{task.employee}</span>
                        <span className="hidden sm:inline">•</span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          <span className="truncate">{task.time}</span>
                        </span>
                      </div>
                    </div>
                    <span
                      className={`px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium whitespace-nowrap flex-shrink-0 ${
                        task.status === 'completed'
                          ? 'bg-green-500/20 text-green-400'
                          : task.status === 'running'
                          ? 'bg-blue-500/20 text-blue-400'
                          : 'bg-yellow-500/20 text-yellow-400'
                      }`}
                    >
                      {task.status}
                    </span>
                  </motion.div>
                ))}
              </div>

              {/* Footer Stats */}
              <div className={`grid grid-cols-3 gap-4 p-4 border-t ${isDark ? 'border-white/5 bg-dark-900/30' : 'border-slate-100 bg-slate-50'}`}>
                {[
                  { label: 'Total Tasks', value: '24' },
                  { label: 'Completed', value: '18' },
                  { label: 'Saved Hours', value: '127' },
                ].map((stat) => (
                  <div key={stat.label} className="text-center">
                    <p className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{stat.value}</p>
                    <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Glow */}
            <div className={`absolute -inset-4 bg-gradient-to-r from-emerald-500/20 via-transparent to-cyan-500/20 rounded-3xl blur-2xl -z-10 ${isDark ? '' : 'opacity-50'}`} />
          </motion.div>

          {/* Text Content */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            className="order-1 lg:order-2"
          >
            <motion.div variants={fadeInUp} className="inline-flex items-center px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-6">
              <Calendar className="w-4 h-4 text-emerald-400 mr-2" />
              <span className="text-sm text-emerald-400">Task Automation</span>
            </motion.div>
            <motion.h2 variants={fadeInUp} className={`text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Set It and{' '}
              <span className="bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
                Forget It
              </span>
            </motion.h2>
            <motion.p variants={fadeInUp} className={`text-lg mb-8 ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
              Schedule recurring tasks and let your AI employees handle them automatically. Weekly reports, daily summaries, competitive analysis — all on autopilot.
            </motion.p>
            <motion.div variants={fadeInUp} className="space-y-4">
              {[
                'Schedule tasks hourly, daily, weekly, or monthly',
                'Get email notifications when tasks complete',
                'Chain multiple AI employees together',
                'Export results to your favorite tools',
              ].map((feature) => (
                <div key={feature} className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                  <span className={isDark ? 'text-gray-300' : 'text-slate-600'}>{feature}</span>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// Brain AI Preview - 3D Style with floating cards
export function BrainAIPreview() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';

  // Data sources that connect to the AI brain
  const dataSources = [
    { icon: '🌐', name: 'Brand Website', subtitle: 'eCommerce, 24 pages', position: 'top' },
    { icon: '📘', name: 'Facebook Insights', subtitle: 'Analytics, 43 reports', position: 'left-top' },
    { icon: '🖼️', name: 'Team Photos', subtitle: 'Company retreat 2024', position: 'right-top' },
    { icon: '💰', name: 'Services Pricing', subtitle: 'sweetpilot.ai, +2 more', position: 'left-mid' },
    { icon: '💬', name: 'Customer Emails', subtitle: 'Follow-up scheduled', position: 'right-mid' },
    { icon: '📋', name: 'Team Handbook', subtitle: '18 pages, guidelines', position: 'left-bottom' },
    { icon: '📄', name: 'Client Proposals', subtitle: '$25k budget', position: 'right-bottom' },
  ];

  return (
    <section className="py-24 lg:py-40 relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0">
        <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full blur-3xl ${isDark ? 'bg-violet-500/5' : 'bg-violet-500/3'}`} />
        <div className={`absolute top-1/3 left-1/4 w-[300px] h-[300px] rounded-full blur-3xl ${isDark ? 'bg-purple-500/10' : 'bg-purple-500/5'}`} />
        <div className={`absolute bottom-1/3 right-1/4 w-[300px] h-[300px] rounded-full blur-3xl ${isDark ? 'bg-pink-500/10' : 'bg-pink-500/5'}`} />
      </div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          className="text-center mb-16 lg:mb-24"
        >
          <motion.h2 variants={fadeInUp} className={`text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`}>
            They learn your business.
            <br />
            <span className="bg-gradient-to-r from-violet-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
              Just like real helpers.
            </span>
          </motion.h2>
          <motion.p variants={fadeInUp} className={`text-lg max-w-3xl mx-auto ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
            Answer questions about your brand, add files, instructions, and your website for more unique results.
            The more information they have, the better the outcome. AI employees are designed to complement
            and enhance your human capabilities by handling routine tasks.
          </motion.p>
        </motion.div>

        {/* Mobile Layout */}
        <div className="lg:hidden">
          {/* Central AI Brain */}
          <motion.div
            className="flex justify-center mb-8"
            initial={{ scale: 0, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.8, delay: 0.2, type: "spring" }}
          >
            <div className="relative">
              {/* Glow ring */}
              <div className="absolute -inset-6 rounded-full bg-gradient-to-r from-violet-500/20 via-purple-500/20 to-pink-500/20 blur-2xl animate-pulse" />

              {/* AI Avatar Container */}
              <div className="relative w-32 h-32">
                {/* Rotating ring */}
                <motion.div
                  className="absolute inset-0 rounded-full border-2 border-dashed border-violet-500/30"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                />

                {/* Inner glow */}
                <div className="absolute inset-3 rounded-full bg-gradient-to-br from-violet-600/40 to-purple-600/40 backdrop-blur-sm" />

                {/* AI Brain Icon */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-violet-500 via-purple-500 to-pink-500 flex items-center justify-center shadow-2xl shadow-purple-500/30">
                    <Brain className="w-12 h-12 text-white" />
                  </div>
                </div>

                {/* Orbiting dots */}
                <motion.div
                  className="absolute inset-0"
                  animate={{ rotate: -360 }}
                  transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
                >
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-2.5 h-2.5 rounded-full bg-cyan-400 shadow-lg shadow-cyan-400/50" />
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-pink-400 shadow-lg shadow-pink-400/50" />
                </motion.div>
              </div>
            </div>
          </motion.div>

          {/* Data Source Cards Grid */}
          <div className="grid grid-cols-2 gap-3">
            {[
              { icon: '🌐', name: 'Brand Website', subtitle: 'eCommerce, 24 pages' },
              { icon: '📘', name: 'Facebook Insights', subtitle: 'Trustpilot, 43 reviews' },
              { icon: '🖼️', name: 'Team Photos', subtitle: 'Company retreat 2024' },
              { icon: '💰', name: 'Services Pricing', subtitle: 'sintra.ai, +2 more' },
              { icon: '💬', name: 'Conversation', subtitle: 'Follow-up scheduled' },
              { icon: '📋', name: 'Team Handbook', subtitle: '18 pages, guidelines' },
            ].map((item, index) => (
              <motion.div
                key={item.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.1 }}
                transition={{ duration: 0.4, delay: 0.1 * index }}
              >
                <DataSourceCard icon={item.icon} name={item.name} subtitle={item.subtitle} />
              </motion.div>
            ))}
          </div>
        </div>

        {/* Desktop Layout - 3D Brain Visualization */}
        <div className="hidden lg:block relative h-[700px]">
          {/* Connection Lines - SVG */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 1 }}>
            <defs>
              <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="rgba(139, 92, 246, 0.3)" />
                <stop offset="100%" stopColor="rgba(139, 92, 246, 0.1)" />
              </linearGradient>
            </defs>
            {/* Lines from left cards to center */}
            <motion.line
              x1="25%" y1="25%" x2="50%" y2="50%"
              stroke="url(#lineGradient)" strokeWidth="2"
              initial={{ pathLength: 0, opacity: 0 }}
              whileInView={{ pathLength: 1, opacity: 1 }}
              viewport={{ once: true, amount: 0.1 }}
              transition={{ duration: 1, delay: 0.5 }}
            />
            <motion.line
              x1="20%" y1="45%" x2="50%" y2="50%"
              stroke="url(#lineGradient)" strokeWidth="2"
              initial={{ pathLength: 0, opacity: 0 }}
              whileInView={{ pathLength: 1, opacity: 1 }}
              viewport={{ once: true, amount: 0.1 }}
              transition={{ duration: 1, delay: 0.6 }}
            />
            <motion.line
              x1="22%" y1="70%" x2="50%" y2="55%"
              stroke="url(#lineGradient)" strokeWidth="2"
              initial={{ pathLength: 0, opacity: 0 }}
              whileInView={{ pathLength: 1, opacity: 1 }}
              viewport={{ once: true, amount: 0.1 }}
              transition={{ duration: 1, delay: 0.7 }}
            />
            {/* Lines from right cards to center */}
            <motion.line
              x1="75%" y1="25%" x2="50%" y2="50%"
              stroke="url(#lineGradient)" strokeWidth="2"
              initial={{ pathLength: 0, opacity: 0 }}
              whileInView={{ pathLength: 1, opacity: 1 }}
              viewport={{ once: true, amount: 0.1 }}
              transition={{ duration: 1, delay: 0.5 }}
            />
            <motion.line
              x1="80%" y1="45%" x2="50%" y2="50%"
              stroke="url(#lineGradient)" strokeWidth="2"
              initial={{ pathLength: 0, opacity: 0 }}
              whileInView={{ pathLength: 1, opacity: 1 }}
              viewport={{ once: true, amount: 0.1 }}
              transition={{ duration: 1, delay: 0.6 }}
            />
            <motion.line
              x1="78%" y1="70%" x2="50%" y2="55%"
              stroke="url(#lineGradient)" strokeWidth="2"
              initial={{ pathLength: 0, opacity: 0 }}
              whileInView={{ pathLength: 1, opacity: 1 }}
              viewport={{ once: true, amount: 0.1 }}
              transition={{ duration: 1, delay: 0.7 }}
            />
          </svg>

          {/* Central AI Character */}
          <motion.div
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10"
            initial={{ scale: 0, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.8, delay: 0.2, type: "spring" }}
          >
            {/* Glow ring */}
            <div className="absolute -inset-8 rounded-full bg-gradient-to-r from-violet-500/20 via-purple-500/20 to-pink-500/20 blur-2xl animate-pulse" />

            {/* AI Avatar Container */}
            <div className="relative w-52 h-52">
              {/* Rotating ring */}
              <motion.div
                className="absolute inset-0 rounded-full border-2 border-dashed border-violet-500/30"
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              />

              {/* Inner glow */}
              <div className="absolute inset-4 rounded-full bg-gradient-to-br from-violet-600/40 to-purple-600/40 backdrop-blur-sm" />

              {/* AI Brain Icon */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-36 h-36 rounded-full bg-gradient-to-br from-violet-500 via-purple-500 to-pink-500 flex items-center justify-center shadow-2xl shadow-purple-500/30">
                  <Brain className="w-20 h-20 text-white" />
                </div>
              </div>

              {/* Orbiting dots */}
              <motion.div
                className="absolute inset-0"
                animate={{ rotate: -360 }}
                transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
              >
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-cyan-400 shadow-lg shadow-cyan-400/50" />
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-pink-400 shadow-lg shadow-pink-400/50" />
              </motion.div>
            </div>
          </motion.div>

          {/* Left Cards */}
          <motion.div
            className="absolute left-[5%] top-[18%]"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <DataSourceCard icon="📘" name="Facebook Insights" subtitle="Trustpilot, 43 reviews" />
          </motion.div>

          <motion.div
            className="absolute left-[2%] top-[42%]"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <DataSourceCard icon="💰" name="Services Pricing" subtitle="sintra.ai, +2 more" />
          </motion.div>

          <motion.div
            className="absolute left-[5%] top-[66%]"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <DataSourceCard icon="📋" name="Team Handbook" subtitle="18 pages, guidelines" />
          </motion.div>

          {/* Right Cards */}
          <motion.div
            className="absolute right-[5%] top-[18%]"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <DataSourceCard icon="🖼️" name="Team Photos" subtitle="Company retreat 2024" />
          </motion.div>

          <motion.div
            className="absolute right-[2%] top-[42%]"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <DataSourceCard icon="💬" name="Conversation with Julia" subtitle="Follow-up scheduled" />
          </motion.div>

          <motion.div
            className="absolute right-[5%] top-[66%]"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <DataSourceCard icon="📄" name="Client Proposal" subtitle="$25k budget" />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// Data Source Card Component
function DataSourceCard({ icon, name, subtitle }: { icon: string; name: string; subtitle: string }) {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';

  return (
    <motion.div
      whileHover={{ scale: 1.05, y: -5 }}
      className={`flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl backdrop-blur-xl border shadow-xl transition-all cursor-pointer group ${isDark ? 'bg-dark-800/80 border-white/10 hover:border-violet-500/30' : 'bg-white border-slate-200 hover:border-violet-500/30'}`}
    >
      <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center text-lg sm:text-xl group-hover:scale-110 transition-transform shrink-0 ${isDark ? 'bg-dark-700/80' : 'bg-slate-100'}`}>
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <p className={`font-medium text-xs sm:text-sm truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>{name}</p>
        <p className={`text-[10px] sm:text-xs truncate ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>{subtitle}</p>
      </div>
    </motion.div>
  );
}

// Integrations Preview - Phone Mockup Style with 3D Effect
export function IntegrationsPreview() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';

  const integrations = [
    { icon: '📘', name: 'Facebook', account: 'Sintra Labs', connected: true, color: 'bg-blue-500' },
    { icon: '📸', name: 'Instagram', account: '', connected: false, color: 'bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400' },
    { icon: '✉️', name: 'Gmail', account: 'help@sweetpilot.ai', connected: true, color: 'bg-red-500' },
    { icon: '💼', name: 'LinkedIn', account: 'sweetpilot.ai', connected: true, color: 'bg-blue-600' },
  ];

  const toolIcons = [
    { icon: '📘', name: 'Facebook', color: 'bg-blue-600' },
    { icon: '📸', name: 'Instagram', color: 'bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400' },
    { icon: '✉️', name: 'Gmail', color: 'bg-red-500' },
    { icon: '📅', name: 'Calendar', color: 'bg-blue-500' },
    { icon: '📧', name: 'Outlook', color: 'bg-blue-500' },
    { icon: '📁', name: 'Drive', color: 'bg-yellow-500' },
    { icon: '🗂️', name: 'Airtable', color: 'bg-cyan-500' },
    { icon: '📝', name: 'Notion', color: 'bg-gray-800' },
  ];

  return (
    <section className="py-24 lg:py-32 relative overflow-hidden">
      <div className="absolute inset-0">
        <div className={`absolute top-1/3 right-1/4 w-[400px] h-[400px] rounded-full blur-3xl ${isDark ? 'bg-blue-500/10' : 'bg-blue-500/5'}`} />
        <div className={`absolute bottom-1/3 left-1/4 w-[300px] h-[300px] rounded-full blur-3xl ${isDark ? 'bg-purple-500/10' : 'bg-purple-500/5'}`} />
      </div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Text Content */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
          >
            <motion.h2 variants={fadeInUp} className={`text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Integrates with your{' '}
              <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                favorite tools.
              </span>
            </motion.h2>
            <motion.p variants={fadeInUp} className={`text-lg mb-8 ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
              <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>Integrations.</span> Streamline business processes by bringing your favorite
              tools, systems, and AI employees together. AI for business makes
              working with integrations and existing systems easier than ever.
            </motion.p>

            {/* Tool Icons Grid */}
            <motion.div
              variants={fadeInUp}
              className="grid grid-cols-4 gap-4"
            >
              {toolIcons.map((tool, index) => (
                <motion.div
                  key={tool.name}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true, amount: 0.1 }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ scale: 1.1, y: -3 }}
                  className={`w-14 h-14 rounded-xl border flex items-center justify-center cursor-pointer transition-all ${isDark ? 'bg-dark-800/80 border-white/10 hover:border-blue-500/30' : 'bg-white border-slate-200 hover:border-blue-500/30 shadow-sm'}`}
                >
                  <span className="text-2xl">{tool.icon}</span>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* 3D Phone Mockup */}
          <div className="relative flex justify-center lg:justify-end" style={{ perspective: '1200px' }}>
            <motion.div
              initial={{ opacity: 0, rotateY: -25, rotateX: 5, x: 60 }}
              whileInView={{ opacity: 1, rotateY: -12, rotateX: 5, x: 0 }}
              viewport={{ once: true, amount: 0.1 }}
              transition={{ duration: 1, ease: "easeOut" }}
              className="relative"
              style={{ transformStyle: 'preserve-3d' }}
            >
              {/* Phone Frame */}
              <div className="relative w-[280px] sm:w-[320px]" style={{ transformStyle: 'preserve-3d' }}>
                {/* Phone Shadow for 3D depth */}
                <div
                  className="absolute inset-0 rounded-[3rem] bg-black/40 blur-2xl"
                  style={{ transform: 'translateZ(-40px) translateX(20px) translateY(20px)' }}
                />

                {/* Phone outer frame - metallic effect */}
                <div
                  className="relative rounded-[3rem] p-[3px] shadow-2xl"
                  style={{
                    background: 'linear-gradient(145deg, #4a4a4a 0%, #2a2a2a 50%, #1a1a1a 100%)',
                    transformStyle: 'preserve-3d'
                  }}
                >
                  {/* Phone body with depth */}
                  <div
                    className="rounded-[2.8rem] bg-gradient-to-b from-gray-800 via-gray-900 to-black p-[2px]"
                    style={{ transformStyle: 'preserve-3d' }}
                  >
                    {/* Phone inner bezel */}
                    <div className="rounded-[2.6rem] bg-black overflow-hidden">
                      {/* Dynamic Island */}
                      <div className="relative">
                        <div className="absolute top-3 left-1/2 -translate-x-1/2 w-28 h-8 bg-black rounded-full z-20 flex items-center justify-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-gray-800" />
                          <div className="w-3 h-3 rounded-full bg-gray-800 ring-1 ring-gray-700" />
                        </div>
                      </div>

                      {/* Screen */}
                      <div className="rounded-[2.4rem] bg-gradient-to-b from-gray-50 to-gray-100 overflow-hidden m-1">
                        {/* Status Bar */}
                        <div className="flex items-center justify-between px-8 pt-4 pb-2">
                          <span className="text-sm font-semibold text-gray-900">9:41</span>
                          <div className="flex items-center gap-1.5">
                            {/* Signal */}
                            <div className="flex items-end gap-0.5">
                              <div className="w-1 h-1 bg-gray-900 rounded-sm" />
                              <div className="w-1 h-1.5 bg-gray-900 rounded-sm" />
                              <div className="w-1 h-2 bg-gray-900 rounded-sm" />
                              <div className="w-1 h-2.5 bg-gray-900 rounded-sm" />
                            </div>
                            {/* Wifi */}
                            <svg className="w-4 h-4 text-gray-900" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M12 18c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2zm-4.9-2.3l1.4 1.4C9.4 18 10.6 18.5 12 18.5s2.6-.5 3.5-1.4l1.4-1.4C15.6 14.6 13.9 14 12 14s-3.6.6-4.9 1.7zM2.5 8.1l1.4 1.4C6.1 7.5 9 6 12 6s5.9 1.5 8.1 3.5l1.4-1.4C18.8 5.6 15.5 4 12 4S5.2 5.6 2.5 8.1z"/>
                            </svg>
                            {/* Battery */}
                            <div className="w-6 h-3 border-2 border-gray-900 rounded-sm flex items-center justify-end pr-0.5 relative">
                              <div className="w-4 h-1.5 bg-gray-900 rounded-sm" />
                              <div className="absolute -right-1 top-1/2 -translate-y-1/2 w-0.5 h-1.5 bg-gray-900 rounded-r-sm" />
                            </div>
                          </div>
                        </div>

                        {/* App Content */}
                        <div className="px-5 pb-10 pt-8 min-h-[480px]">
                          {/* Header */}
                          <div className="flex items-center gap-3 mb-8">
                            <div className="w-11 h-11 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-xl shadow-lg">
                              🤖
                            </div>
                            <span className="text-xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                              Brain AI
                            </span>
                          </div>

                          {/* Integrations Title */}
                          <h3 className="text-2xl font-bold text-gray-900 mb-5">Integrations</h3>

                          {/* Integration Cards */}
                          <div className="space-y-3">
                            {integrations.map((integration, index) => (
                              <motion.div
                                key={integration.name}
                                initial={{ opacity: 0, x: 20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true, amount: 0.1 }}
                                transition={{ delay: 0.4 + index * 0.1 }}
                                className="flex items-center justify-between p-3.5 bg-white rounded-2xl shadow-sm border border-gray-100"
                              >
                                <div className="flex items-center gap-3">
                                  <div className={`w-12 h-12 rounded-xl ${integration.color} flex items-center justify-center text-xl shadow-md`}>
                                    {integration.icon}
                                  </div>
                                  <div>
                                    <p className="font-semibold text-gray-900">{integration.name}</p>
                                    {integration.account && (
                                      <p className="text-xs text-gray-500">{integration.account}</p>
                                    )}
                                  </div>
                                </div>
                                {integration.connected ? (
                                  <span className="px-3.5 py-1.5 rounded-full bg-green-100 text-green-600 text-xs font-semibold">
                                    Added
                                  </span>
                                ) : (
                                  <button className="px-3.5 py-1.5 rounded-full bg-purple-100 text-purple-600 text-xs font-semibold hover:bg-purple-200 transition-colors">
                                    Add
                                  </button>
                                )}
                              </motion.div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Phone Side Buttons - adjusted for 3D */}
                <div className="absolute -left-[2px] top-28 w-[3px] h-8 bg-gradient-to-r from-gray-600 to-gray-700 rounded-l-lg" />
                <div className="absolute -left-[2px] top-40 w-[3px] h-14 bg-gradient-to-r from-gray-600 to-gray-700 rounded-l-lg" />
                <div className="absolute -left-[2px] top-56 w-[3px] h-14 bg-gradient-to-r from-gray-600 to-gray-700 rounded-l-lg" />
                <div className="absolute -right-[2px] top-36 w-[3px] h-16 bg-gradient-to-l from-gray-600 to-gray-700 rounded-r-lg" />

                {/* Enhanced Glow Effect */}
                <div className="absolute -inset-12 bg-gradient-to-r from-blue-500/30 via-purple-500/20 to-pink-500/30 rounded-full blur-3xl -z-10 animate-pulse" />
                <div className="absolute -inset-6 bg-gradient-to-tr from-cyan-500/20 to-transparent rounded-full blur-2xl -z-10" />
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

// Always On Preview - Hand holding phone with 3D AI character
export function AlwaysOnPreview() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';

  return (
    <section className={`py-24 lg:py-40 relative overflow-hidden ${isDark ? 'bg-black' : 'bg-slate-900'}`}>
      {/* Subtle gradient overlay */}
      <div className={`absolute inset-0 ${isDark ? 'bg-gradient-to-b from-dark-900 via-black to-black' : 'bg-gradient-to-b from-slate-900 via-slate-950 to-slate-900'}`} />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          {/* 3D Hand with Phone */}
          <motion.div
            initial={{ opacity: 0, x: -60 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="relative flex justify-center lg:justify-start"
            style={{ perspective: '1500px' }}
          >
            <div className="relative" style={{ transformStyle: 'preserve-3d' }}>
              {/* Hand silhouette holding phone */}
              <div className="relative">
                {/* Hand shadow/silhouette - left side */}
                <div className="absolute -left-16 top-20 w-24 h-[400px] z-10">
                  <svg viewBox="0 0 100 400" className="w-full h-full">
                    <defs>
                      <linearGradient id="handGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#0a0a0a" />
                        <stop offset="100%" stopColor="#1a1a1a" />
                      </linearGradient>
                    </defs>
                    {/* Thumb */}
                    <path
                      d="M80 80 Q95 100 90 140 Q85 180 75 200 Q65 220 50 230 L40 220 Q55 200 60 170 Q65 140 60 100 Q55 70 80 80"
                      fill="url(#handGradient)"
                    />
                    {/* Palm and fingers wrapping around */}
                    <path
                      d="M20 120 Q10 150 15 200 Q20 280 30 350 Q35 380 50 390 L70 390 Q60 370 55 340 Q45 280 45 200 Q45 150 55 120 Q60 100 45 100 Q30 100 20 120"
                      fill="url(#handGradient)"
                    />
                  </svg>
                </div>

                {/* Hand shadow/silhouette - right side */}
                <div className="absolute -right-12 top-32 w-20 h-[350px] z-10">
                  <svg viewBox="0 0 80 350" className="w-full h-full">
                    {/* Fingers on right side */}
                    <path
                      d="M10 100 Q0 130 5 180 Q10 250 20 320 Q25 350 40 360 L55 350 Q45 320 40 270 Q35 200 35 150 Q35 110 25 90 Q20 80 10 100"
                      fill="url(#handGradient)"
                    />
                  </svg>
                </div>

                {/* Phone */}
                <motion.div
                  initial={{ rotateY: 15, rotateX: -5 }}
                  whileInView={{ rotateY: 8, rotateX: -3 }}
                  viewport={{ once: true, amount: 0.1 }}
                  transition={{ duration: 1.5, ease: "easeOut" }}
                  className="relative w-[280px] sm:w-[300px] z-20"
                  style={{ transformStyle: 'preserve-3d' }}
                >
                  {/* Phone outer frame */}
                  <div className="relative rounded-[2.5rem] p-[3px] shadow-2xl bg-gradient-to-b from-gray-800 via-gray-900 to-black">
                    {/* Phone bezel */}
                    <div className="rounded-[2.3rem] bg-black overflow-hidden">
                      {/* Dynamic Island */}
                      <div className="absolute top-3 left-1/2 -translate-x-1/2 w-28 h-7 bg-black rounded-full z-30 flex items-center justify-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-gray-900" />
                        <div className="w-3 h-3 rounded-full bg-gray-900 ring-1 ring-gray-800" />
                      </div>

                      {/* Screen with AI character */}
                      <div className="relative rounded-[2rem] overflow-hidden m-[2px]">
                        {/* Background - City skyline gradient */}
                        <div className="absolute inset-0 bg-gradient-to-b from-pink-300 via-purple-400 to-blue-500">
                          {/* City silhouette */}
                          <div className="absolute bottom-0 left-0 right-0 h-32">
                            <svg viewBox="0 0 400 100" className="w-full h-full" preserveAspectRatio="none">
                              <path
                                d="M0 100 L0 70 L20 70 L20 50 L40 50 L40 60 L60 60 L60 30 L80 30 L80 45 L100 45 L100 20 L120 20 L120 40 L140 40 L140 55 L160 55 L160 35 L180 35 L180 25 L200 25 L200 45 L220 45 L220 30 L240 30 L240 50 L260 50 L260 40 L280 40 L280 55 L300 55 L300 35 L320 35 L320 60 L340 60 L340 45 L360 45 L360 70 L380 70 L380 50 L400 50 L400 100 Z"
                                fill="rgba(0,0,0,0.3)"
                              />
                            </svg>
                          </div>
                        </div>

                        {/* App header */}
                        <div className="relative z-10 flex items-center justify-between px-4 pt-12 pb-3">
                          <div className="flex items-center gap-2">
                            <motion.div
                              animate={{ scale: [1, 1.1, 1] }}
                              transition={{ duration: 2, repeat: Infinity }}
                              className="w-2 h-2 rounded-full bg-green-400"
                            />
                            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-pink-400 to-purple-500 flex items-center justify-center overflow-hidden border-2 border-white/30">
                              <span className="text-lg">🎀</span>
                            </div>
                            <div>
                              <p className="text-white font-semibold text-sm drop-shadow-lg">Aria</p>
                              <p className="text-white/80 text-xs drop-shadow-md">Your Social Media Manager</p>
                            </div>
                          </div>
                          <div className="w-8 h-8 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                            <MessageSquare className="w-4 h-4 text-white" />
                          </div>
                        </div>

                        {/* 3D AI Character */}
                        <div className="relative h-[380px] flex items-center justify-center overflow-hidden">
                          <CharacterWrapper>
                            <AriaCharacter size="lg" />
                          </CharacterWrapper>
                        </div>

                        {/* Message input */}
                        <div className="absolute bottom-4 left-4 right-4 z-20">
                          <div className="flex items-center gap-3 px-4 py-3 bg-white/95 backdrop-blur-xl rounded-full shadow-lg">
                            <Paperclip className="w-5 h-5 text-gray-400" />
                            <span className="text-gray-400 text-sm flex-1">Send Aria a message</span>
                            <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
                              <Send className="w-4 h-4 text-white" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Phone buttons */}
                  <div className="absolute -left-[2px] top-24 w-[3px] h-8 bg-gray-800 rounded-l-lg" />
                  <div className="absolute -left-[2px] top-36 w-[3px] h-12 bg-gray-800 rounded-l-lg" />
                  <div className="absolute -left-[2px] top-52 w-[3px] h-12 bg-gray-800 rounded-l-lg" />
                  <div className="absolute -right-[2px] top-32 w-[3px] h-14 bg-gray-800 rounded-r-lg" />
                </motion.div>

                {/* Ambient glow */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[500px] bg-gradient-to-br from-pink-500/20 via-purple-500/15 to-blue-500/20 rounded-full blur-3xl -z-10" />
              </div>
            </div>
          </motion.div>

          {/* Text Content */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            className="lg:pl-8"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-8 leading-tight"
            >
              A co-worker<br />
              who's always on<br />
              the clock.
            </motion.h2>

            <motion.div variants={fadeInUp} className="space-y-6">
              <div>
                <p className="text-gray-300 text-lg">
                  <span className="text-white font-semibold">Available 24/7.</span>{' '}
                  AI tools are always on and available around the clock to support your business.
                  The only helpers who love overtime. Always ready to save your most valuable asset—your time.
                </p>
              </div>

              <div>
                <p className="text-gray-300 text-lg">
                  <span className="text-white font-semibold">Speaks in 100+ languages.</span>{' '}
                  Go global—select, communicate, and complete your work in over 100 languages.
                </p>
              </div>
            </motion.div>

            {/* Feature badges */}
            <motion.div
              variants={fadeInUp}
              className="flex flex-wrap gap-3 mt-8"
            >
              {['24/7 Support', '100+ Languages', 'Instant Response', 'Never Tired'].map((badge) => (
                <span
                  key={badge}
                  className="px-4 py-2 rounded-full bg-white/5 border border-white/10 text-gray-400 text-sm"
                >
                  {badge}
                </span>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// Automates Work Section - Colorful cards with AI employees
export function AutomatesWorkPreview() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';

  return (
    <section className="py-24 lg:py-32 relative overflow-hidden">
      {/* Background with subtle gradient */}
      <div className={`absolute inset-0 ${isDark ? 'bg-gradient-to-b from-dark-900 via-gray-900/50 to-dark-900' : 'bg-gradient-to-b from-slate-50 via-white to-slate-50'}`} />

      {/* Decorative blurred background */}
      <div className={`absolute top-0 left-0 right-0 h-64 bg-gradient-to-b to-transparent ${isDark ? 'from-gray-800/30' : 'from-slate-200/30'}`} />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          className="text-center mb-16"
        >
          <motion.h2
            variants={fadeInUp}
            className={`text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`}
          >
            Automates work.
            <br />
            <span className={isDark ? 'text-gray-400' : 'text-slate-500'}>Even while you sleep.</span>
          </motion.h2>
          <motion.p
            variants={fadeInUp}
            className={`text-lg max-w-3xl mx-auto ${isDark ? 'text-gray-400' : 'text-slate-600'}`}
          >
            Automate tasks with business automation tools—create social media posts, respond to
            comments, and more—freeing your team from repetitive tasks so they can focus on more
            strategic work.
          </motion.p>
        </motion.div>

        {/* Main Feature Card - Purple */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.8 }}
          className="mb-8"
        >
          <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-violet-500 via-purple-500 to-purple-600 p-8 lg:p-12">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              {/* Text Content */}
              <div className="relative z-10">
                <h3 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                  Aria, schedule social<br />
                  media posts for me
                </h3>
                <p className="text-white/80 text-lg">
                  Automate your social media game with AI for marketing. Write, create, and post content
                  effortlessly with AI-powered solutions.
                </p>
              </div>

              {/* Phone Mockup */}
              <div className="relative flex justify-center lg:justify-end">
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true, amount: 0.1 }}
                  transition={{ delay: 0.3, duration: 0.8 }}
                  className="relative"
                >
                  {/* Phone */}
                  <div className="w-[260px] rounded-[2.5rem] bg-black p-[3px] shadow-2xl">
                    <div className="rounded-[2.3rem] bg-black overflow-hidden">
                      {/* Dynamic Island */}
                      <div className="absolute top-2 left-1/2 -translate-x-1/2 w-24 h-6 bg-black rounded-full z-20" />

                      {/* Screen */}
                      <div className="rounded-[2rem] bg-white overflow-hidden m-[2px]">
                        {/* Status Bar */}
                        <div className="flex items-center justify-between px-6 pt-3 pb-1 bg-white">
                          <span className="text-xs font-semibold text-gray-900">9:41</span>
                          <div className="flex items-center gap-1">
                            <div className="flex items-end gap-0.5">
                              <div className="w-0.5 h-1 bg-gray-900" />
                              <div className="w-0.5 h-1.5 bg-gray-900" />
                              <div className="w-0.5 h-2 bg-gray-900" />
                              <div className="w-0.5 h-2.5 bg-gray-900" />
                            </div>
                            <div className="w-4 h-2 border border-gray-900 rounded-sm ml-1">
                              <div className="w-3 h-1 bg-gray-900 m-0.5 rounded-sm" />
                            </div>
                          </div>
                        </div>

                        {/* App Content */}
                        <div className="px-4 pb-6 pt-4">
                          {/* Header */}
                          <div className="flex items-center justify-between mb-4">
                            <h4 className="text-lg font-bold text-gray-900">Review posts</h4>
                            <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center">
                              <svg className="w-3 h-3 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                              </svg>
                            </div>
                          </div>

                          {/* Post Preview */}
                          <div className="bg-gray-50 rounded-2xl p-3 border border-gray-100">
                            <div className="flex items-center gap-2 mb-3">
                              <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center">
                                <span className="text-white text-xs font-bold">f</span>
                              </div>
                              <div>
                                <p className="text-sm font-semibold text-gray-900">SweetPilot • Product Launch</p>
                              </div>
                            </div>

                            {/* Image placeholder with gradient */}
                            <div className="w-full h-32 rounded-xl bg-gradient-to-br from-orange-400 via-pink-400 to-blue-400 mb-3 flex items-center justify-center overflow-hidden">
                              <div className="text-center">
                                <span className="text-4xl">🏄</span>
                              </div>
                            </div>

                            <p className="text-xs text-gray-600 leading-relaxed">
                              We've been quietly building something special - an app that brings the power
                              to your business, working 24/7 so you don't have to.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 3D Character overlay */}
                  <div className="absolute -right-4 bottom-4 scale-75">
                    <CharacterWrapper>
                      <AriaCharacter size="md" />
                    </CharacterWrapper>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Two Column Cards */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Blue Card - Customer Support */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-600 p-8"
          >
            <div className="relative z-10">
              <h3 className="text-2xl lg:text-3xl font-bold text-white mb-3">
                Nova, check my<br />
                Facebook comments
              </h3>
              <p className="text-white/80 text-sm leading-relaxed mb-6">
                Engage your audience with business automation tools. Use AI for customer support
                to analyze comments and craft personalized responses. By automating comment analysis,
                your team can focus on building stronger customer relationships.
              </p>
            </div>

            {/* Mini Phone Mockup */}
            <div className="relative">
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true, amount: 0.1 }}
                transition={{ delay: 0.4, duration: 0.8 }}
                className="flex items-end justify-between"
              >
                {/* Phone */}
                <div className="w-[180px] rounded-[1.5rem] bg-black p-[2px] shadow-xl">
                  <div className="rounded-[1.4rem] bg-gray-900 overflow-hidden">
                    {/* Status bar */}
                    <div className="flex items-center justify-between px-4 pt-2 pb-1">
                      <span className="text-[10px] text-white">9:41</span>
                      <div className="w-14 h-4 bg-black rounded-full" />
                      <div className="flex items-center gap-0.5">
                        <div className="w-3 h-1.5 border border-white rounded-sm">
                          <div className="w-2 h-0.5 bg-white m-0.5" />
                        </div>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="px-3 py-2">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-pink-400 to-purple-500 flex items-center justify-center">
                          <span className="text-[10px]">💙</span>
                        </div>
                        <div>
                          <p className="text-[10px] text-white font-medium">Nova • Customer Support</p>
                          <p className="text-[8px] text-gray-400">SweetPilot Brain</p>
                        </div>
                      </div>

                      <p className="text-[10px] text-gray-400 mb-2">Suggested action</p>

                      <div className="flex gap-2">
                        <div className="flex-1 bg-blue-500/30 rounded-xl p-2">
                          <div className="w-5 h-5 rounded-lg bg-blue-400/50 flex items-center justify-center mb-1">
                            <MessageSquare className="w-3 h-3 text-white" />
                          </div>
                          <p className="text-[9px] text-white font-medium">Review 2</p>
                          <p className="text-[8px] text-white/70">Comments</p>
                        </div>
                        <div className="flex-1 bg-white/10 rounded-xl p-2">
                          <div className="flex items-center gap-1 mb-1">
                            <div className="w-2 h-2 rounded-full bg-green-400" />
                            <span className="text-[8px] text-green-400">Active</span>
                          </div>
                          <p className="text-[9px] text-white font-medium">Auto-</p>
                          <p className="text-[8px] text-white/70">Commenter</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Mini Character - Nova */}
                <div className="scale-50 -mr-8 -mb-6">
                  <CharacterWrapper floatDelay={0.2}>
                    <NovaCharacter size="md" />
                  </CharacterWrapper>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Yellow/Gold Card - Meetings */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-amber-400 via-orange-400 to-yellow-500 p-8"
          >
            <div className="relative z-10">
              <h3 className="text-2xl lg:text-3xl font-bold text-white mb-3">
                Atlas, help me prepare<br />
                for today's meetings
              </h3>
              <p className="text-white/90 text-sm leading-relaxed mb-6">
                Boost productivity with AI. Streamline business processes with daily summaries based on your
                email and calendar to keep your schedule on track.
              </p>
            </div>

            {/* Cards Stack */}
            <div className="relative">
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true, amount: 0.1 }}
                transition={{ delay: 0.5, duration: 0.8 }}
                className="flex items-end justify-between"
              >
                {/* Stacked notification cards */}
                <div className="space-y-2">
                  <div className="bg-white rounded-xl p-3 shadow-lg w-52">
                    <div className="flex items-start gap-2">
                      <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                        <span className="text-sm">🤖</span>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-gray-900">2 actions needed</p>
                        <p className="text-[10px] text-gray-500">Approve new hire offer letter, sign vendor contract by EOD</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-3 shadow-lg w-44 ml-4">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-lg bg-gray-100 flex items-center justify-center">
                        <Calendar className="w-3 h-3 text-gray-600" />
                      </div>
                      <p className="text-xs font-semibold text-gray-900">3 meetings</p>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-3 shadow-lg w-36 ml-8">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-lg bg-gray-100 flex items-center justify-center">
                        <FileText className="w-3 h-3 text-gray-600" />
                      </div>
                      <p className="text-xs font-semibold text-gray-900">14 emails</p>
                    </div>
                  </div>
                </div>

                {/* Character - Atlas */}
                <div className="scale-50 -mr-8 -mb-6">
                  <CharacterWrapper floatDelay={0.5}>
                    <AtlasCharacter size="md" />
                  </CharacterWrapper>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// Infinite Knowledge Section - Hand holding phone with AI character
export function InfiniteKnowledgePreview() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';

  return (
    <section className="py-24 lg:py-40 relative overflow-hidden bg-slate-900">
      {/* Background elements */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-900 via-blue-950/30 to-slate-900">
        <div className="absolute top-1/4 right-1/4 w-[500px] h-[500px] bg-blue-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 left-1/4 w-[400px] h-[400px] bg-purple-500/10 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Text Content */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
          >
            <motion.h2
              variants={fadeInUp}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight"
              style={{ color: '#ffffff' }}
            >
              Your new team<br />
              with infinite<br />
              <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                knowledge.
              </span>
            </motion.h2>

            <motion.p
              variants={fadeInUp}
              className="text-lg mb-8 max-w-lg"
              style={{ color: '#9ca3af' }}
            >
              AI for business has never been this personal—meet your new team,
              working hard so you don't have to.
            </motion.p>

            <motion.a
              variants={fadeInUp}
              href="/register"
              className="inline-flex items-center px-8 py-4 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-semibold hover:from-blue-600 hover:to-cyan-600 transition-all shadow-lg shadow-blue-500/25"
            >
              Get SweetPilot
            </motion.a>
          </motion.div>

          {/* 3D Hand with Phone */}
          <motion.div
            initial={{ opacity: 0, x: 60 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="relative flex justify-center lg:justify-end"
            style={{ perspective: '1500px' }}
          >
            <div className="relative" style={{ transformStyle: 'preserve-3d' }}>
              {/* Hand silhouette holding phone - right side */}
              <div className="absolute -right-20 top-16 w-32 h-[450px] z-30">
                <svg viewBox="0 0 120 450" className="w-full h-full">
                  <defs>
                    <linearGradient id="handGradientRight" x1="100%" y1="0%" x2="0%" y2="0%">
                      <stop offset="0%" stopColor="#0a0a0a" />
                      <stop offset="100%" stopColor="#1a1a1a" />
                    </linearGradient>
                  </defs>
                  {/* Thumb on front */}
                  <path
                    d="M20 100 Q5 130 10 180 Q15 230 25 260 Q35 290 50 300 L60 290 Q45 270 40 230 Q35 180 40 140 Q45 100 20 100"
                    fill="url(#handGradientRight)"
                  />
                  {/* Palm and fingers wrapping around */}
                  <path
                    d="M100 140 Q110 180 105 250 Q100 340 90 400 Q85 430 70 440 L50 440 Q60 410 65 370 Q75 300 75 220 Q75 170 65 140 Q60 110 75 110 Q90 110 100 140"
                    fill="url(#handGradientRight)"
                  />
                </svg>
              </div>

              {/* Hand on left side */}
              <div className="absolute -left-16 top-24 w-28 h-[400px] z-10">
                <svg viewBox="0 0 100 400" className="w-full h-full">
                  {/* Fingers on left side */}
                  <path
                    d="M80 120 Q90 160 85 220 Q80 300 70 360 Q65 390 50 400 L35 390 Q45 360 50 300 Q55 220 55 170 Q55 130 65 110 Q70 90 80 120"
                    fill="url(#handGradientRight)"
                  />
                </svg>
              </div>

              {/* Phone */}
              <motion.div
                initial={{ rotateY: -15, rotateX: 5 }}
                whileInView={{ rotateY: -8, rotateX: 3 }}
                viewport={{ once: true, amount: 0.1 }}
                transition={{ duration: 1.5, ease: "easeOut" }}
                className="relative w-[280px] sm:w-[320px] z-20"
                style={{ transformStyle: 'preserve-3d' }}
              >
                {/* Phone outer frame */}
                <div className="relative rounded-[2.5rem] p-[3px] shadow-2xl bg-gradient-to-b from-gray-700 via-gray-800 to-black">
                  {/* Phone bezel */}
                  <div className="rounded-[2.3rem] bg-black overflow-hidden">
                    {/* Dynamic Island */}
                    <div className="absolute top-3 left-1/2 -translate-x-1/2 w-28 h-7 bg-black rounded-full z-30 flex items-center justify-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-gray-900" />
                      <div className="w-3 h-3 rounded-full bg-gray-900 ring-1 ring-gray-800" />
                    </div>

                    {/* Screen */}
                    <div className="relative rounded-[2rem] overflow-hidden m-[2px]">
                      {/* Background gradient */}
                      <div className="absolute inset-0 bg-gradient-to-b from-cyan-400 via-blue-500 to-indigo-600">
                        {/* Grid pattern overlay */}
                        <div className="absolute inset-0 opacity-20" style={{
                          backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
                          backgroundSize: '20px 20px'
                        }} />
                        {/* City/tech silhouette */}
                        <div className="absolute bottom-0 left-0 right-0 h-24">
                          <svg viewBox="0 0 400 80" className="w-full h-full" preserveAspectRatio="none">
                            <path
                              d="M0 80 L0 50 L30 50 L30 30 L50 30 L50 40 L80 40 L80 20 L100 20 L100 35 L130 35 L130 15 L150 15 L150 30 L180 30 L180 45 L200 45 L200 25 L230 25 L230 40 L260 40 L260 20 L280 20 L280 35 L310 35 L310 50 L340 50 L340 30 L370 30 L370 55 L400 55 L400 80 Z"
                              fill="rgba(0,0,0,0.4)"
                            />
                          </svg>
                        </div>
                      </div>

                      {/* App header */}
                      <div className="relative z-10 px-4 pt-14 pb-4">
                        {/* Top pills */}
                        <div className="flex justify-between mb-4">
                          <div className="px-3 py-1.5 rounded-full bg-white/20 backdrop-blur-sm">
                            <span className="text-white text-xs font-medium">Swipe through new daily ideas</span>
                          </div>
                          <div className="px-3 py-1.5 rounded-full bg-white/20 backdrop-blur-sm">
                            <span className="text-white text-xs font-medium">Improve results with Brain AI</span>
                          </div>
                        </div>

                        {/* Card */}
                        <div className="bg-white/95 backdrop-blur-xl rounded-2xl p-4 shadow-xl mt-4">
                          <p className="text-gray-600 text-xs mb-2">Stay on track</p>

                          {/* AI Character container */}
                          <div className="relative h-40 flex items-center justify-center mb-4 bg-gradient-to-b from-gray-50 to-white rounded-xl overflow-hidden">
                            <div className="scale-75">
                              <CharacterWrapper>
                                <AtlasCharacter size="md" />
                              </CharacterWrapper>
                            </div>
                          </div>

                          {/* Task card */}
                          <div className="bg-purple-50 rounded-xl p-3 mb-3">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-sm">👑</span>
                              <span className="text-xs text-purple-600 font-medium">Today's Picks</span>
                            </div>
                            <p className="text-sm font-semibold text-gray-900">Build workflows with Atlas</p>
                          </div>

                          {/* Action card */}
                          <div className="flex items-center justify-between bg-gray-50 rounded-xl p-3">
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-400 to-cyan-400 flex items-center justify-center">
                                <span className="text-xs">📋</span>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Outline local cafe</p>
                                <p className="text-sm font-medium text-gray-900">growth strategy</p>
                              </div>
                            </div>
                            <button className="px-3 py-1.5 rounded-full bg-blue-500 text-white text-xs font-medium">
                              Start
                            </button>
                          </div>
                        </div>

                        {/* Bottom nav */}
                        <div className="flex justify-around mt-4 pt-4 border-t border-white/20">
                          {[
                            { icon: '🏠', label: 'Home', active: true },
                            { icon: '📅', label: 'Today', active: false },
                            { icon: '🧠', label: 'Brain AI', active: false },
                          ].map((item) => (
                            <div key={item.label} className="flex flex-col items-center gap-1">
                              <span className={`text-lg ${item.active ? 'opacity-100' : 'opacity-60'}`}>{item.icon}</span>
                              <span className={`text-[10px] ${item.active ? 'text-white' : 'text-white/60'}`}>{item.label}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Phone buttons */}
                <div className="absolute -left-[2px] top-24 w-[3px] h-8 bg-gray-700 rounded-l-lg" />
                <div className="absolute -left-[2px] top-36 w-[3px] h-12 bg-gray-700 rounded-l-lg" />
                <div className="absolute -left-[2px] top-52 w-[3px] h-12 bg-gray-700 rounded-l-lg" />
                <div className="absolute -right-[2px] top-32 w-[3px] h-14 bg-gray-700 rounded-r-lg" />
              </motion.div>

              {/* Ambient glow */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[450px] h-[550px] bg-gradient-to-br from-blue-500/25 via-cyan-500/20 to-purple-500/25 rounded-full blur-3xl -z-10" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// FAQ Section
export function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs = [
    {
      question: "What can I use SweetPilot for?",
      answer: "SweetPilot provides AI employees that can handle a wide range of business tasks including social media management, customer support, content creation, email management, data analysis, meeting preparation, and much more. Each AI employee specializes in different areas to give you comprehensive business automation."
    },
    {
      question: "Is there a money-back guarantee?",
      answer: "Yes! We offer a 14-day money-back guarantee on all plans. If you're not satisfied with SweetPilot for any reason, simply contact our support team within 14 days of your purchase for a full refund."
    },
    {
      question: "Can I invite my team to use SweetPilot?",
      answer: "Absolutely! Our Business and Enterprise plans support team collaboration. You can invite team members, assign specific AI employees to different departments, and manage permissions all from one dashboard."
    },
    {
      question: "Does SweetPilot have an affiliate program?",
      answer: "Yes, we have a generous affiliate program that rewards you for spreading the word about SweetPilot. Earn commissions for every new customer you refer. Contact our partnerships team to learn more and get started."
    },
    {
      question: "Are there tutorials or guides to help me use SweetPilot?",
      answer: "We provide comprehensive documentation, video tutorials, and onboarding guides to help you get the most out of SweetPilot. Our AI assistant Sage is also available 24/7 to answer any questions and guide you through features."
    },
    {
      question: "Can SweetPilot integrate with other software I use?",
      answer: "Yes! SweetPilot integrates with popular tools like Gmail, Slack, Facebook, Instagram, LinkedIn, Google Calendar, Notion, Airtable, and many more. We're constantly adding new integrations based on user requests."
    },
  ];

  return (
    <section className="py-24 lg:py-32 relative overflow-hidden bg-gradient-to-b from-gray-50 to-white">
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left side - Title */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
          >
            <motion.h2
              variants={fadeInUp}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6"
            >
              Questions?<br />
              <span className="text-gray-400">Let's clear things up.</span>
            </motion.h2>

            <motion.p
              variants={fadeInUp}
              className="text-gray-600 text-lg mb-4"
            >
              Yes, we understand—AI-powered solutions, business automation tools,
              AI for marketing, AI for customer support... a lot of big words can get
              confusing.
            </motion.p>

            <motion.p
              variants={fadeInUp}
              className="text-gray-600 text-lg"
            >
              We're here to make it clear—check out our FAQs, and if you still feel the
              need to ask AI questions, Sage is always ready to answer.
            </motion.p>
          </motion.div>

          {/* Right side - FAQ items */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            className="space-y-4"
          >
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="border-b border-gray-200"
              >
                <button
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                  className="w-full py-5 flex items-center justify-between text-left group"
                >
                  <span className="text-lg font-medium text-gray-900 group-hover:text-blue-600 transition-colors pr-4">
                    {faq.question}
                  </span>
                  <motion.div
                    animate={{ rotate: openIndex === index ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                    className="shrink-0"
                  >
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  </motion.div>
                </button>

                <AnimatePresence>
                  {openIndex === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <p className="pb-5 text-gray-600 leading-relaxed">
                        {faq.answer}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export default {
  DashboardStatsPreview,
  ChatInterfacePreview,
  TaskAutomationPreview,
  BrainAIPreview,
  IntegrationsPreview,
  AlwaysOnPreview,
  AutomatesWorkPreview,
  InfiniteKnowledgePreview,
  FAQSection,
};
